/**
 * One user of the super admin panel (`admin.users.detail`, #4418): the person's record (`user`) and the data of the tab
 * that is open (`tab`). The `tab` section carries ONLY the requested tab, as a union discriminated by `tab`: a person
 * who opens «Seguridad» does not pay for the audit trail. A user that does not exist is the 404 of the whole screen,
 * not a failed section.
 */

import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'
import { adminPaginationSchema, systemUserRowSchema } from './users.screen.js'

export const userDetailTabSchema = z.enum([
  'profile',
  'security',
  'sessions',
  'activity',
  'audit',
  'gdpr',
  'notes',
])
export type UserDetailTab = z.infer<typeof userDetailTabSchema>

export const userResourceUsageSchema = z.strictObject({
  transactionCount: z.number(),
  importCount: z.number(),
  reportCount: z.number(),
  dataSourceCount: z.number(),
})

export const loginHistoryEntrySchema = z.strictObject({
  id: z.string(),
  action: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  timestamp: z.string(),
  success: z.boolean(),
})

export const userSessionRowSchema = z.strictObject({
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  expiresAt: z.string(),
  isActive: z.boolean(),
  deviceType: z.enum(['desktop', 'mobile', 'tablet', 'unknown']),
  browser: z.string().nullable(),
  os: z.string().nullable(),
  location: z.string().nullable(),
  isCurrent: z.boolean(),
  deviceName: z.string().nullable(),
})

export const userActivityEntrySchema = z.strictObject({
  id: z.string(),
  type: z.enum(['transaction', 'import', 'report', 'login', 'other']),
  action: z.string().optional(),
  resourceType: z.string().optional(),
  resourceName: z.string().nullable().optional(),
  description: z.string(),
  timestamp: z.string(),
  metadata: jsonDocument({
    reason:
      'audit_log.new_values behind an activity entry: the keys are written by each audited route, so they are free',
  }).optional(),
})

/** A row of the audit trail of the person: five keys, never the old and new values nor the address. */
export const userAuditRowSchema = z.strictObject({
  id: z.string(),
  action: z.string(),
  resourceType: z.string(),
  resourceName: z.string().nullable(),
  createdAt: z.string(),
})
export type UserAuditRow = z.infer<typeof userAuditRowSchema>

export const gdprStatusSchema = z.strictObject({
  hasPendingExport: z.boolean(),
  hasPendingDeletion: z.boolean(),
  lastExportDate: z.string().nullable(),
  deletionScheduledFor: z.string().nullable(),
})

export const adminNoteSchema = z.strictObject({
  id: z.string(),
  content: z.string(),
  isPinned: z.boolean(),
  author: z.strictObject({ id: z.string(), email: z.string(), name: z.string() }),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const userDetailTabDataSchema = z.discriminatedUnion('tab', [
  z.strictObject({
    tab: z.literal('profile'),
    /** `null`: the user belongs to no organisation (it has no accounting data); never invented zeros. */
    usage: userResourceUsageSchema.nullable(),
  }),
  z.strictObject({
    tab: z.literal('security'),
    loginHistory: z.strictObject({
      items: z.array(loginHistoryEntrySchema),
      pagination: adminPaginationSchema,
      failedLast24h: z.number(),
    }),
  }),
  z.strictObject({ tab: z.literal('sessions'), sessions: z.array(userSessionRowSchema) }),
  z.strictObject({
    tab: z.literal('activity'),
    items: z.array(userActivityEntrySchema),
    pagination: adminPaginationSchema,
  }),
  z.strictObject({
    tab: z.literal('audit'),
    items: z.array(userAuditRowSchema),
    pagination: adminPaginationSchema,
  }),
  z.strictObject({ tab: z.literal('gdpr'), gdprStatus: gdprStatusSchema }),
  z.strictObject({
    tab: z.literal('notes'),
    items: z.array(adminNoteSchema),
    pagination: adminPaginationSchema,
  }),
])
export type UserDetailTabData = z.infer<typeof userDetailTabDataSchema>

/**
 * `page` and `limit` serve «Seguridad», «Actividad», «Auditoría» and «Notas»; 50 is the lowest cap of the old routes
 * (`/activity`).
 */
export const adminUserDetailQuerySchema = z.strictObject({
  id: z.guid(),
  tab: userDetailTabSchema.default('profile'),
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(50).default(20),
})

export const adminUserDetailScreen = defineScreen({
  id: 'admin.users.detail',
  path: '/api/screens/admin/users/detail',
  access: 'super_admin',
  query: adminUserDetailQuerySchema,
  response: z.strictObject({
    user: systemUserRowSchema,
    tab: screenSection(userDetailTabDataSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.users'],
  budget: { requests: 1, statements: 10, bytes: 40_000, p95Ms: 200 },
  summary: 'Users › one user: the profile and the data of the open tab',
})
export type AdminUserDetailQuery = ScreenQuery<typeof adminUserDetailScreen>
export type AdminUserDetailData = ScreenData<typeof adminUserDetailScreen>
